/**
 * Message Sender Module for InterPals Private Messages
 * Handles message composition, validation, and sending with photo attachments
 */

// Helper to get translation (falls back to key if i18n not available)
function t(key) {
    return window.t ? window.t(key) : key;
}

// Configuration
const MessageSenderConfig = {
  SCROLL_DELAY: 200,
  FOCUS_DELAY: 200,
  SUCCESS_MESSAGE_DELAY: 1000,
  ENDPOINT: '/pm.php'
};

// Error messages (use getters for lazy translation loading)
const MessageSenderErrors = {
  get SEND_FAILED() { return t('common.msg_send_failed'); },
  get NETWORK_ERROR() { return t('common.msg_network_error'); },
  get INVALID_THREAD() { return t('common.msg_invalid_thread'); },
  get SEND_IN_PROGRESS() { return t('common.msg_send_in_progress'); }
};

/**
 * Message data validator
 */
class MessageDataValidator {
  static validate(messageData) {
    const errors = [];

    // Must have either text or photo
    if (!messageData.hasText && !messageData.hasPhoto) {
      return errors; // Return empty array - this is handled by UI
    }

    // Thread ID must be numeric
    if (!$.isNumeric(messageData.threadId)) {
      errors.push(MessageSenderErrors.INVALID_THREAD);
    }

    return errors;
  }
}

/**
 * Message sending service
 */
class MessageSendingService {
  static async sendMessage(threadId, msgText, photoId) {
    const data = {
      "action": "send_message",
      "thread": threadId,
      "message": msgText
    };
    
    if (photoId) {
      data.photo_id = String(photoId);
    }

    return new Promise((resolve, reject) => {
      // Set ajax request marker
      window.pm_ajax_request_active = 1;
      
      $.ajax({
        type: "POST",
        url: MessageSenderConfig.ENDPOINT,
        data: data,
        dataType: "json"
      }).done((response) => {
        if (response.success) {
          resolve(response);
        } else {
          reject(new Error(response.error || MessageSenderErrors.SEND_FAILED));
        }
      }).fail((xhr, status, error) => {
        console.error('Send failed:', status, error, xhr.responseText);
        reject(new Error(`${MessageSenderErrors.NETWORK_ERROR}: ${error}`));
      }).always(() => {
        window.pm_ajax_request_active = 0;
      });
    });
  }
}

/**
 * UI state manager for send operations
 */
class SendUIManager {
  constructor() {
    this.elements = {
      submitBtn: $("#msg_submit"),
      messageInput: $("#message"),
      sendButtonText: $("#send_button_text")
    };
  }

  setSendingState() {
    this.elements.submitBtn.prop("disabled", true);
    this.elements.messageInput.prop("disabled", true);
    this.elements.sendButtonText.text(t('common.sending'));
    this.elements.submitBtn.attr('aria-label', t('common.sending_message'));
  }

  setReadyState() {
    this.elements.submitBtn.prop("disabled", false);
    this.elements.messageInput.prop("disabled", false);
    this.elements.sendButtonText.text(t('common.send'));
    this.elements.submitBtn.attr('aria-label', t('common.send_message'));
  }
}

/**
 * Message display manager
 */
class MessageDisplayManager {
  static appendMessage(messageBody, threadsRight) {
    // Use existing append_msgs function which handles everything including scrolling
    window.append_msgs(messageBody, threadsRight);
  }

  static scrollToBottom(threadsRight) {
    setTimeout(() => {
      if (typeof threadsRight.scrollTo === "function" && threadsRight.length) {
        threadsRight.scrollTo({
          top: threadsRight.get(0).scrollHeight,
          left: 0,
          behavior: "smooth"
        });
      }
    }, MessageSenderConfig.SCROLL_DELAY);
  }

  static focusMessageBox() {
    // Skip auto-focus on mobile to avoid reopening the keyboard
    if (!window.matchMedia('(min-width: 640px)').matches) return;
    setTimeout(() => {
      $("#message").focus();
    }, MessageSenderConfig.FOCUS_DELAY);
  }
}

/**
 * Main message sender class
 */
class MessageSender {
  constructor() {
    this.isSending = false;
    this.uiManager = new SendUIManager();
  }

  /**
   * Main entry point for sending messages
   */
  async sendMessage() {
    if (this.isSending) {
      console.warn('Message send already in progress');
      return false;
    }

    const messageData = this.gatherMessageData();
    
    // Early return if no content
    if (!messageData.hasText && !messageData.hasPhoto) {
      return false;
    }

    // Validate message data
    const validationErrors = MessageDataValidator.validate(messageData);
    if (validationErrors.length > 0) {
      this.showError(validationErrors[0]);
      return false;
    }

    this.isSending = true;
    this.uiManager.setSendingState();

    try {
      if (messageData.hasText && messageData.hasPhoto) {
        await this.sendTextAndPhotoSeparately(messageData);
      } else {
        await this.sendSingleMessage(messageData);
      }
      
      this.handleSendSuccess(messageData);
    } catch (error) {
      this.handleSendError(error);
    } finally {
      this.isSending = false;
      this.uiManager.setReadyState();
    }

    return false; // Prevent form submission
  }

  /**
   * Gather all necessary data for sending
   */
  gatherMessageData() {
    const msgBlock = $("#message");
    const msgText = $.trim(msgBlock.val());
    const threadId = $("#send_thread_id").val();
    const selectedFile = window.photoUpload?.getSelectedFile() || null;
    const existingPhotoId = window.photoUpload?.getPhotoId() || null;

    return {
      msgBlock,
      msgText,
      threadId,
      selectedFile,
      existingPhotoId,
      hasText: msgText.length > 0,
      hasPhoto: selectedFile !== null || existingPhotoId !== null,
      threadsRight: $("#threads_right"),
      threadsMain: $("#threads_main")
    };
  }

  /**
   * Send text and photo as separate messages
   */
  async sendTextAndPhotoSeparately(messageData) {
    let photoIdToSend = messageData.existingPhotoId;
    
    // Upload photo if needed
    if (messageData.selectedFile && !messageData.existingPhotoId) {
      photoIdToSend = await window.photoUpload.uploadPhoto(messageData.selectedFile);
    }

    // Send text message first
    const textResponse = await MessageSendingService.sendMessage(
      messageData.threadId, 
      messageData.msgText, 
      null
    );
    MessageDisplayManager.appendMessage(textResponse.body, messageData.threadsRight);
    
    // Send photo message
    const photoResponse = await MessageSendingService.sendMessage(
      messageData.threadId, 
      '', 
      photoIdToSend
    );
    MessageDisplayManager.appendMessage(photoResponse.body, messageData.threadsRight);
  }

  /**
   * Send a single message (text only or photo only)
   */
  async sendSingleMessage(messageData) {
    let photoIdToSend = messageData.existingPhotoId;
    
    // Upload photo if needed
    if (messageData.selectedFile && !messageData.existingPhotoId) {
      photoIdToSend = await window.photoUpload.uploadPhoto(messageData.selectedFile);
    }

    const response = await MessageSendingService.sendMessage(
      messageData.threadId, 
      messageData.msgText, 
      photoIdToSend
    );
    MessageDisplayManager.appendMessage(response.body, messageData.threadsRight);
  }

  /**
   * Handle successful message sending
   */
  handleSendSuccess(messageData) {
    // Clear draft from cache and local storage
    if (typeof last_draft[messageData.threadId] !== "undefined") {
      delete last_draft[messageData.threadId];
    }
    window.draftStorage?.remove(messageData.threadId);

    // Note: Scrolling is handled by append_msgs function automatically
    // No need to call scrollToBottom here to avoid conflicts

    // Clear message input and reset textarea height
    messageData.msgBlock.val("");
    messageData.msgBlock.css("height", "");
    messageData.msgBlock[0].style.height = 'auto';

    // Clear photo if present
    if (window.photoUpload?.hasPhoto()) {
      window.photoUpload.removePhoto();
    }

    // Collapse textarea
    messageData.threadsMain.removeClass("send");
    
    // Focus message box
    MessageDisplayManager.focusMessageBox();
  }

  /**
   * Handle send errors
   */
  handleSendError(error) {
    console.error('Error sending message:', error);
    this.showError(error.message || MessageSenderErrors.SEND_FAILED);
  }

  /**
   * Show error message to user
   */
  showError(message) {
    // TODO: Replace alert with better UI notification
    alert(message);
  }
}

// Global initialization
let messageSender;

function initMessageSender() {
  messageSender = new MessageSender();
  return messageSender;
}

// Legacy global function for backward compatibility
window.send_msg = function() {
  if (!messageSender) {
    messageSender = initMessageSender();
  }
  return messageSender.sendMessage();
};

// Auto-initialize
if (typeof $ !== 'undefined') {
  $(document).ready(() => {
    if ($(".privateMessages").length > 0) {
      initMessageSender();
    }
  });
}

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    MessageSender,
    MessageSenderConfig,
    MessageSenderErrors,
    MessageDataValidator,
    MessageSendingService,
    MessageDisplayManager,
    SendUIManager
  };
} 